import type { ReactNode } from "react";
import { EmptyState } from "@/components/EmptyState";
import {
  DataTable as RdsDataTable,
  Loading,
  type ColumnDef,
  type LucideIconName,
} from "@/deps/ui-library";

/**
 * One column. `align: "right"` and `numeric` are separate on purpose: a quantity is right-aligned
 * but not tabular, and a document number is tabular but left-aligned.
 */
export interface Column<Row> {
  key: string;
  header: string;
  /** Right for amounts, so the decimal points line up down the column. */
  align?: "left" | "right";
  /** Digits that line up, through RDS's `numeric` column. */
  numeric?: boolean;
  /** Column width, as a CSS grid track. Omit to size to content. */
  width?: string;
  render: (row: Row) => ReactNode;
}

/** The shape EmptyState takes. Named so the two empty states cannot drift apart. */
export interface EmptyStateCopy {
  icon: LucideIconName;
  title: string;
  description: string;
  action?: ReactNode;
}

/**
 * Which page of a server-paged list is showing. The state lives in the URL, so the table only
 * reports a change — it never keeps a page of its own.
 */
export interface TablePaging {
  /** 1-based, as the URL has it. */
  page: number;
  size: number;
  total: number;
  sizes: readonly number[];
  onPageChange: (page: number) => void;
  onSizeChange: (size: number) => void;
}

export interface DataTableProps<Row> {
  columns: Array<Column<Row>>;
  rows: Row[];
  rowKey: (row: Row) => string;
  onRowClick?: (row: Row) => void;
  /**
   * What a clickable row is called, for anyone not looking at it: RDS puts it on the row's
   * `aria-label`, so arriving on the row by Tab announces the invoice rather than reading four
   * cells that all start the same way.
   */
  rowLabel?: (row: Row) => string;
  /** Shown while the first page is loading. Subsequent pages keep the old rows visible. */
  loading?: boolean;
  /**
   * What to say when there are no rows and no filters — "nothing here yet". A list screen leaves this
   * to `ListScreen`, which never shows the table without rows.
   */
  empty?: EmptyStateCopy;
  /**
   * What to say when there are no rows *because of* the filters. A different sentence and a
   * different next action: the answer is "clear them", not "create one".
   */
  filteredEmpty?: EmptyStateCopy;
  filtered?: boolean;
  caption: string;
  /** Server paging under the table. Without it every row given is shown. */
  paging?: TablePaging;
}

/**
 * The list table used by Invoices, Credit notes, Receipts, Bills and Fund transfers: RDS
 * `DataTable`.
 *
 * A real `<table>`, not a grid of divs: screen readers announce row and column position from it,
 * and "row 12 of 40, Amount, 1,13,000" is the difference between a usable list and a wall of text.
 * A clickable row is in the tab order and opens on Enter or Space (RDS does both).
 */
export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowLabel,
  loading = false,
  empty,
  filteredEmpty,
  filtered = false,
  caption,
  paging,
}: DataTableProps<Row>) {
  // Which of the two empties this is changes the words and the action, so it is decided here
  // rather than left to every screen to remember.
  const state = filtered && filteredEmpty ? filteredEmpty : empty;

  return (
    <RdsDataTable
      aria-label={loading ? `${caption} — loading` : caption}
      size="sm"
      enableSorting={false}
      columns={columns.map(toColumnDef)}
      data={rows}
      getRowId={(row) => rowKey(row)}
      onRowClick={onRowClick ? (row) => onRowClick(row.original) : undefined}
      rowLabel={rowLabel ? (row) => rowLabel(row.original) : undefined}
      loading={loading}
      states={{ loading: <TableSkeleton columns={columns.length} /> }}
      emptyContent={
        state ? (
          <EmptyState
            bare
            icon={state.icon}
            title={state.title}
            description={state.description}
            action={state.action}
          />
        ) : undefined
      }
      pagination={
        paging
          ? {
              // Manual: the rows given are the page, already cut by the server.
              mode: "manual",
              page: paging.page - 1,
              pageSize: paging.size,
              totalItems: paging.total,
              onPageChange: (index) => paging.onPageChange(index + 1),
              onPageSizeChange: paging.onSizeChange,
              pageSizeOptions: [...paging.sizes],
              // Nothing to page through while loading or when nothing matched.
              show: !loading && paging.total > 0,
              showInfo: true,
              showPageSize: true,
              showGoTo: false,
            }
          : { mode: "manual", show: false }
      }
    />
  );
}

function toColumnDef<Row>(column: Column<Row>): ColumnDef<Row> {
  return {
    id: column.key,
    header: column.header,
    cell: ({ row }) => column.render(row.original),
    meta: {
      textAlign: column.align === "right" ? "right" : "left",
      // A set width here was a short, fixed column — a date, a number. Hugging keeps it one line.
      hugContent: column.width !== undefined,
      numeric: column.numeric,
    },
  };
}

/**
 * The loading state keeps the table's height.
 *
 * Bars of the right height, so the page does not jump when the data lands — a spinner in the
 * middle of an empty box moves everything below it twice. Hidden from assistive technology: the
 * bars convey nothing, and the table's name already says it is loading.
 */
export function TableSkeleton({ columns }: { columns: number }) {
  return (
    <div aria-hidden="true" className="flex flex-col gap-3 py-2">
      {Array.from({ length: 8 }, (_row, row) => (
        <div key={row} className="flex gap-4">
          {Array.from({ length: columns }, (_cell, column) => (
            <Loading.Skeleton key={column} variant="text" className="h-3 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}
